import React, { useState, useEffect, useRef } from "react";
import { Music, Play, Pause, ExternalLink, ShoppingCart, Headphones } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHABA_AVATAR = "https://customer-assets.emergentagent.com/job_jonesaica-portal/artifacts/h7rpnpq7_IMG_4438.jpg";
const SHABA_INTRO_VIDEO = "https://customer-assets.emergentagent.com/job_8ed5ec52-990e-4af6-908a-8b1627b4f2b5/artifacts/7owgx5f1_shaba%20shara%20intro.mp4";

const SHABA_CASHAPP = "https://cash.app/$Jonesaicayahsharal";

function ShabaMusicPlayer({ song }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const { addItem } = useCart();

  const previewUrl = song.preview_file_url || song.audio_file_url;
  const maxDuration = song.preview_duration_seconds || 20;

  const togglePlay = () => {
    if (!previewUrl) return;
    if (playing) {
      audioRef.current?.pause();
      clearInterval(timerRef.current);
      setPlaying(false);
    } else {
      const audio = audioRef.current;
      audio.currentTime = 0;
      audio.play().catch(() => {});
      setPlaying(true);
      setProgress(0);
      timerRef.current = setInterval(() => {
        if (audio.currentTime >= maxDuration) {
          audio.pause();
          clearInterval(timerRef.current);
          setPlaying(false);
          setProgress(0);
        } else {
          setProgress((audio.currentTime / maxDuration) * 100);
        }
      }, 100);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => { clearInterval(timerRef.current); audio?.pause(); };
  }, []);

  return (
    <Card className="card-official overflow-hidden" data-testid={`shaba-song-${song.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-royal-purple text-white flex items-center justify-center hover:bg-royal-purple/80 transition flex-shrink-0">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-royal-blue truncate">{song.title}</h3>
            <p className="text-xs text-slate-500">Shaba Sharah</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-kingdom-gold rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-slate-400">{maxDuration}s</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="font-bold text-royal-blue">${song.price?.toFixed(2)}</span>
            <div className="mt-1">
              <button
                type="button"
                className="btn-gold text-xs px-3 py-1 rounded font-semibold"
                onClick={() => addItem({
                  internal_id: song.id,
                  name: song.title,
                  price: Number(song.price || 0.99),
                  type: "one_time",
                  image: song.cover_image_url || null,
                })}
                data-testid={`buy-shaba-${song.id}`}
              >
                Add ${(song.price || 0.99).toFixed(2)}
              </button>
            </div>
          </div>
        </div>
        <audio ref={audioRef} src={previewUrl} preload="none" />
      </CardContent>
    </Card>
  );
}

export default function BrandShaba() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchMusic = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        const musicProducts = response.data.filter(p => p.product_type === "music");
        setSongs(musicProducts);
      } catch {
        toast.error("Failed to load music");
      } finally {
        setLoading(false);
      }
    };
    fetchMusic();
  }, []);

  return (
    <div className="min-h-screen" data-testid="shaba-page">
      {/* Hero — dark dramatic for strong text contrast */}
      <section className="relative text-white py-20 overflow-hidden bg-royal-blue">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/40 via-transparent to-blue-900/60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="relative flex-shrink-0" data-testid="brandshaba-hero-media">
              <video
                src={SHABA_INTRO_VIDEO}
                poster={SHABA_AVATAR}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-56 h-56 rounded-full object-cover border-4 border-kingdom-gold shadow-xl bg-slate-900"
                data-testid="brandshaba-intro-video"
              />
            </div>
            <div>
              <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Artist</span>
              <h1 className="heading-hero mt-2 mb-4 text-white">Shaba Sharah</h1>
              <p className="text-purple-200 text-sm mb-2">a.k.a. Naby'ah Shaba Sharah Tzadaq'ah</p>
              <p className="body-large text-purple-100 max-w-xl">
                Scripture-based reggae and dancehall music rooted in Torah truth.
                Prophetic fire meets Caribbean rhythm.
              </p>
              <div className="flex gap-3 mt-6">
                <a href="https://youtube.com/@shabasharah?si=94NEL8uvqAqjsCg4" target="_blank" rel="noopener noreferrer" data-testid="brandshaba-youtube-link">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10">
                    <Music className="w-4 h-4 mr-2" />YouTube
                  </Button>
                </a>
                <a href="https://www.tiktok.com/@shabasharah" target="_blank" rel="noopener noreferrer" data-testid="brandshaba-tiktok-link">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10">
                    <Headphones className="w-4 h-4 mr-2" />TikTok
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Music Section */}
      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Discography</span>
            <h2 className="heading-section mt-2 text-royal-blue">Music</h2>
            <p className="text-slate-600 mt-2">
              Preview 20 seconds of each track. Purchase for full download.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8"><p className="text-slate-500">Loading music...</p></div>
          ) : songs.length === 0 ? (
            <div className="text-center py-8"><p className="text-slate-500">No music available yet.</p></div>
          ) : (
            <div className="space-y-4">
              {songs.map(song => <ShabaMusicPlayer key={song.id} song={song} />)}
            </div>
          )}

          <div className="text-center mt-8">
            <a href="/shop">
              <Button className="btn-gold">
                <ShoppingCart className="w-4 h-4 mr-2" />View Full Shop
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Support Tiers — Music Patron Packages */}
      <section className="bg-royal-blue py-16" data-testid="brandshaba-support-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Support the Artist</span>
            <h2 className="heading-section mt-2 mb-4 text-white">Monthly Patron Tiers</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Subscribe monthly to receive exclusive downloads, early releases, and direct support to Shaba Sharah.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Supporter */}
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-lg text-center relative" data-testid="tier-supporter">
              <h3 className="text-white text-xl font-bold">Supporter</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$10<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6 text-left">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Early access to new releases</li>
              </ul>
              <button
                type="button"
                className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold"
                onClick={() => addItem({ internal_id: "supporter_monthly", name: "Supporter (Monthly)", price: 10, type: "subscription" })}
                data-testid="brandshaba-tier-subscribe-supporter-monthly"
              >
                Subscribe Monthly
              </button>
            </div>
            {/* Builder (popular) */}
            <div className="bg-slate-950 border-2 border-kingdom-gold p-6 rounded-lg text-center relative" data-testid="tier-builder">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-kingdom-gold text-royal-blue text-xs font-bold px-3 py-1 rounded-full">POPULAR</div>
              <h3 className="text-white text-xl font-bold">Builder</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$25<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6 text-left">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Exclusive content access</li>
                <li>&bull; Monthly video calls</li>
              </ul>
              <button
                type="button"
                className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold"
                onClick={() => addItem({ internal_id: "builder_monthly", name: "Builder (Monthly)", price: 25, type: "subscription" })}
                data-testid="brandshaba-tier-subscribe-builder-monthly"
              >
                Subscribe Monthly
              </button>
            </div>
            {/* Patron */}
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-lg text-center relative" data-testid="tier-patron">
              <h3 className="text-white text-xl font-bold">Patron</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$50<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6 text-left">
                <li>&bull; All Builder benefits</li>
                <li>&bull; One-time merch item</li>
                <li>&bull; Priority support</li>
              </ul>
              <button
                type="button"
                className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold"
                onClick={() => addItem({ internal_id: "patron_monthly", name: "Patron (Monthly)", price: 50, type: "subscription" })}
                data-testid="brandshaba-tier-subscribe-patron-monthly"
              >
                Subscribe Monthly
              </button>
            </div>
          </div>

          {/* Cash App Direct Tip */}
          <div className="mt-10 max-w-2xl mx-auto bg-gradient-to-r from-kingdom-gold/20 to-purple-700/20 border border-kingdom-gold/40 rounded-lg p-6 text-center" data-testid="brandshaba-cashapp-block">
            <p className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider mb-2">Tip the Artist Directly</p>
            <p className="text-slate-300 text-sm mb-4">Send one-time support straight to Shaba Sharah on Cash App.</p>
            <a href={SHABA_CASHAPP} target="_blank" rel="noopener noreferrer" data-testid="brandshaba-cashapp-link">
              <Button className="btn-gold">$Jonesaicayahsharal on Cash App</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Etsy Merch */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Merchandise</span>
          <h2 className="heading-section mt-2 mb-4">Official Merch</h2>
          <p className="text-slate-300 mb-6">T-shirts, hoodies, and more available on Etsy.</p>
          <a href="https://www.etsy.com/shop/AncientPathFactory" target="_blank" rel="noopener noreferrer">
            <Button className="btn-gold">
              <ExternalLink className="w-4 h-4 mr-2" />Shop on Etsy
            </Button>
          </a>
        </div>
      </section>

      {/* Bio */}
      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">About</span>
            <h2 className="heading-section mt-2 text-royal-blue">The Artist</h2>
          </div>
          <Card className="card-official">
            <CardContent className="p-8">
              <p className="text-slate-700 leading-relaxed">
                Shaba Sharah is a prophetic reggae and dancehall artist from The Kingdom of Yunah'Shach'Qal Yashar'al.
                Her music draws from scripture, Torah observance, and the rich rhythms of Caribbean sound system culture.
                Each track is a declaration of amunah and identity, blending spiritual warfare with island flare.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4">
                The Babylon Contract Mash Up collection features songs of freedom, covenant, and deliverance — 
                from Pasach Skank to Blood Pan Di Doorpost, each track carries prophetic weight and 
                musical fire.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
